// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Measure from 'react-measure';
import { CLS_PREFIX } from '../../constants';
import ButtonGroup from '../ButtonGroup';
import Button from '../Button';

import '../../helpers/base.less';
import './SegmentedControl.less';

const alphaSort = (a, b) => a > b ? 1 : -1;

/**
 *  `Segmented Control` is a group of several [Buttons](#!/Button) typically used for switching between
 * different content views (list, tiles, and so on). Since there can only be one current content view for a
 * particular content, one of the buttons in a group is always active, and only one button should be active at a time.
 * The buttons are displayed in-line and can contain text, images or both.
 */
class SegmentedControl extends Component {
    constructor(props) {
        super(props);

        const { selected } = props;
        this.state = { selected, collapsed: false, monoWidth: 0, monoWidthButton: null };

        this.items = {};
        this.maxWidth = null;
        this.minWidth = null;
    }

    getChildContext() {
        return {
            responsive: this.isResponsive(),
        };
    }

    componentDidMount() {
        this.loadItems();
    }

    componentWillReceiveProps(props) {
        const { selected, multiple } = props;
        const newState = {};

        newState.selected = this.state.selected;
        if (selected !== this.props.selected) {
            newState.selected = selected;
        }

        if (multiple && newState.selected && !(newState.selected instanceof Array)) {
            newState.selected = [newState.selected];
        }

        if (newState.selected !== this.state.selected) {
            this.setState(newState);
        }
    }

    componentDidUpdate() {
        this.loadItems();
    }

    isResponsive() {
        return typeof this.context.responsive === 'undefined' ? this.state.collapsed : this.context.responsive;
    }

    loadItems() {
        setTimeout(() => {
            if (!this.groupRef) {
                return;
            }

            const { baseClassName, buttons } = this.props;
            const items = Array.prototype.map.call(
                this.groupRef.querySelectorAll(`.${baseClassName}__button`),
                el => el.getBoundingClientRect().width
            );

            this.items = items.reduce(
                (res, width, ind) => {
                    res[buttons[ind].value] = width;
                    return res;
                },
                {},
            );
            this.itemsWidth = items.reduce((res, el) => res + el, 0);

            this.checkAdaptive();
        }, 0);
    }

    checkAdaptive = () => {
        if (!this.group || !this.items || Object.keys(this.items).length !== this.props.buttons.length) {
            return;
        }

        const { buttons, monospaced } = this.props;
        const { collapsed, monoWidth, monoWidthButton } = this.state;
        const newState = {};

        if (!this.maxWidth || this.itemsWidth > this.maxWidth) {
            this.maxWidth = this.itemsWidth;
        }
        if (!this.minWidth || this.itemsWidth < this.minWidth) {
            this.minWidth = this.itemsWidth;
        }

        if (!collapsed && monospaced) {
            let width = 0;
            let widthButton = null;
            for (let i = 0; i < buttons.length; i++) {
                const button = buttons[i].value;
                const size = this.items[button];

                if (size > monoWidth && size > width) {
                    width = size;
                    widthButton = button;
                }
            }

            if (width && width !== monoWidth) {
                newState.monoWidth = width;
            }
            if (widthButton && widthButton !== monoWidthButton) {
                newState.monoWidthButton = widthButton;
            }
        }

        const offset = this.groupRef.getBoundingClientRect().left - this.group.left;
        if (!collapsed && this.group.width < this.itemsWidth + offset) {
            newState.collapsed = true;
        } else if (collapsed && this.group.width >= this.maxWidth + offset) {
            newState.collapsed = false;
        }

        if (Object.keys(newState).length) {
            this.setState(newState, () => {
                setTimeout(this.checkAdaptive, 0);
            });
        }
    };

    handleChange = value => () => {
        const { multiple, onChange } = this.props;
        const { selected } = this.state;

        let newSelected;
        if (multiple) {
            newSelected = selected ? selected.slice() : [];
            if (!selected || !(selected instanceof Array) || selected.indexOf(value) === -1) {
                newSelected.push(value);
            } else {
                newSelected.splice(selected.indexOf(value), 1);
            }

            newSelected.sort(alphaSort);
        } else {
            newSelected = value;
        }

        if (selected instanceof Array) {
            selected.sort(alphaSort);
        }

        if (JSON.stringify(newSelected) !== JSON.stringify(selected)) {
            this.setState({ selected: newSelected });

            onChange(newSelected);
        }
    };

    handleResize = contentRect => {
        this.group = contentRect.bounds;
        this.checkAdaptive();
    };

    renderButtonGroup({ buttons, monospaced, multiple, onChange, baseClassName }) {
        const { selected, monoWidth, monoWidthButton } = this.state;
        const isCollapsed = this.isResponsive();

        return (
            <ButtonGroup
                innerRef={ref => (this.groupRef = ref)}
                className={`${baseClassName}__button-group`}
            >
                {buttons.map(({ value, title, ...buttonProps }) => {
                    // uncontrolled
                    const handler = {};
                    if (onChange !== null) {
                        handler.onClick = this.handleChange(value);
                    }

                    let buttonSelected = false;
                    if (multiple && selected && selected instanceof Array && selected.indexOf(value) !== -1) {
                        buttonSelected = true;
                    } else if (!multiple && selected === value) {
                        buttonSelected = true;
                    }

                    buttonProps.className = classNames(buttonProps.className, `${baseClassName}__button`);

                    if (!isCollapsed && monospaced && monoWidth && monoWidthButton !== value) {
                        buttonProps.style = {
                            width: monoWidth,
                            ...buttonProps.style,
                        };
                    }

                    return (
                        <Button
                            key={value}
                            selected={buttonSelected}
                            {...handler}
                            {...buttonProps}
                        >
                            {title}
                        </Button>
                    );
                })}
            </ButtonGroup>
        );
    }

    render() {
        const {
            label,
            labelPlacement,
            buttons,
            monospaced,
            multiple,
            selected: selectedProp,
            onChange,
            inline,
            className,
            baseClassName,
            ...props
        } = this.props;

        const isCollapsed = this.isResponsive();

        return (
            <Measure onResize={this.handleResize} bounds>
                {({ measureRef }) => (
                    <div
                        ref={measureRef}
                        className={classNames(
                            baseClassName,
                            {
                                [`${baseClassName}--inline`]: inline,
                                [`${baseClassName}--collapsed`]: isCollapsed,
                                [`${baseClassName}--vertical`]: label && labelPlacement === 'top',
                            },
                            className,
                        )}
                        {...props}
                    >
                        {label && (
                            <div className={`${baseClassName}__label`}>
                                {label}
                            </div>
                        )}

                        {this.renderButtonGroup({ buttons, monospaced, multiple, onChange, baseClassName })}
                    </div>
                )}
            </Measure>
        );
    }
}

SegmentedControl.propTypes = {
    /**
     * Label
     */
    label: PropTypes.node,
    /**
     * Where to place label
     */
    labelPlacement: PropTypes.oneOf(['top', 'left']),
    /**
     * Array of buttons to display (every array item must contain an unique "value" property)
     */
    buttons: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.any.isRequired,
        title: PropTypes.node,
    })).isRequired,
    /**
     * Monospaced version of buttons (all button would have the same width).
     * Attention: using it with buttons dynamic update may cause to wrong behavior!
     */
    monospaced: PropTypes.bool,
    /**
     * Multiple selected options version (any number of buttons can be selected)
     */
    multiple: PropTypes.bool,
    /**
     * Selected value or an array of values (for multiple version).
     * Used in controlled version or as default value in uncontrolled version (with onChange property)
     */
    selected: PropTypes.oneOfType([
        PropTypes.any,
        PropTypes.arrayOf(PropTypes.any),
    ]).isRequired,
    /**
     * On change selection callback for uncontrolled mode.
     */
    onChange: PropTypes.func,
    /**
     * Should component displays as inline element?
     */
    inline: PropTypes.bool,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};

SegmentedControl.defaultProps = {
    label: null,
    labelPlacement: 'top',
    monospaced: false,
    multiple: false,
    onChange: null,
    inline: false,
    className: null,
    baseClassName: `${CLS_PREFIX}segmented-control`,
};

SegmentedControl.contextTypes = {
    responsive: PropTypes.bool,
};

SegmentedControl.childContextTypes = {
    responsive: PropTypes.bool,
};

export default SegmentedControl;



// WEBPACK FOOTER //
// ./components/SegmentedControl/SegmentedControl.js